import React, { FunctionComponent, useMemo } from 'react';
import Benefits, { BenefitProps } from '@/components/Benefits/Benefits';
import VisalexFeatures from '@/components/GreenCards/Common/VisalexFeatures/VisalexFeatures';
import { Layout } from '@/components/Layout';
import WhatOffer from '@/components/Visas/WhatOffer/WhatOffer';
import { Col, Container, createArrayInRange, Hero, LinkButton, Row } from '@visalex/core';

import cx from 'clsx';
import { Trans, useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import styles from '@/components/Visas/WorkVisa.module.scss';

const prefix = 'work-visa';

const WorkVisa: FunctionComponent = () => {
  const { t, i18n } = useTranslation();
  const benefits: BenefitProps[] = useMemo(
    () =>
      createArrayInRange(0, 5).map((index) => ({
        key: index,
        title: t(`${prefix}.benefits.${index}.title`),
        description: t(`${prefix}.benefits.${index}.description`),
        imageName: `${prefix}/icon_${index + 1}.svg`,
      })),
    [i18n.language]
  );

  return (
    <Layout title={t(`${prefix}.seo-title`)} description={t(`${prefix}.seo-description`)}>
      <div className={cx(styles[`${prefix}-landing`], styles[`${prefix}`])}>
        <Hero className={styles[`${prefix}__hero`]}>
          <h1>{t(`${prefix}.hero.title`)}</h1>
          <p>
            <Trans i18nKey={`${prefix}.hero.for-whom`}>
              Whether you are <strong>an employer</strong> who needs to move workers to the U.S.,{' '}
              <strong>an employee</strong> seeking to  join your firm’s U.S. subsidiary,{' '}
              <strong>an entrepreneur investor</strong>, or <strong>another type of foreign professional</strong>,
              VisaLex is here to help. With our comprehensive tools and services, you can apply for an{' '}
              <strong>L-1, H-1B, E-2, or other</strong> U.S. work visa with far less risk and effort, and a lot more
              confidence.
            </Trans>
          </p>
          <LinkButton size="lg" href="/free-consultation" className={styles[`${prefix}__hero_button`]}>
            {t('nav-bar.schedule-consultation')}
          </LinkButton>
          <small>{t('common.supported-locales')}</small>
        </Hero>

        <Container>
          <Row className={styles['introducing-visalex__content']}>
            <Col xl={8} className="mx-auto">
              <h2>{t(`${prefix}.intro.title`)}</h2>
              <p>{t(`${prefix}.intro.description`)}</p>
            </Col>
          </Row>
          <Row className={styles[`${prefix}__benefits`]}>
            <Benefits benefits={benefits} colProps={{ xs: 12, md: 6, lg: 4 }} />
          </Row>

          <WhatOffer />

          <VisalexFeatures
            className={styles['included-services']}
            i18nPrefix={`${prefix}.included-services`}
            featureIds={createArrayInRange(0, 5)}
            buttonClass={styles['included-services__tab']}
          />
          <VisalexFeatures
            i18nPrefix={`${prefix}.platform-advantages`}
            featureIds={createArrayInRange(0, 4)}
            revertedSlider
            buttonClass={styles['included-services__tab']}
          />
        </Container>
        {/* <SuccessRate i18nPrefix="work-visa" />
      <VisalexFeatures i18nPrefix="work-visa" />
      <SeoFooter i18nPrefix="work-visa" /> */}
      </div>
    </Layout>
  );
};

export async function getStaticProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await serverSideTranslations(locale)),
    },
  };
}

export default WorkVisa;
